import React, { useCallback, useEffect, useState } from 'react';

import { Alert, Badge, Button, Spinner, Table } from '../../../core/ui';
import { useTranslation } from '../../../core/context/TranslationContext';

import { deleteInvitation, listInvitations, revokeInvitation } from '../api/family';

import InviteCreateModal from './InviteCreateModal';
import styles from './InviteList.module.css';

export default function InviteList() {
  const { t } = useTranslation();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const handleCopy = (inv) => {
    navigator.clipboard.writeText(inv.url);
    setCopiedId(inv.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setInvitations(await listInvitations());
    } catch (err) {
      setError(err.response?.data?.message || t('error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const handleRevoke = async (id) => {
    if (!window.confirm(t('family.invite.confirmRevoke'))) return;
    try {
      await revokeInvitation(id);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t('error'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('family.invite.confirmDelete'))) return;
    setDeletingId(id);
    setError(null);
    try {
      await deleteInvitation(id);
      setInvitations(current => current.filter(inv => inv.id !== id));
    } catch (err) {
      setError(t('family.invite.deleteError'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreated = async () => {
    await load();
    setShowToast(true);
  };

  if (loading) return <Spinner label={t('loading')} className={styles.spinner} />;

  return (
    <div>
      {error && <Alert variant="danger" role="alert">{error}</Alert>}
      <Button variant="primary" size="sm" className={styles.createBtn} onClick={() => setShowModal(true)}>
        {t('family.invite.create')}
      </Button>
      <Table striped hover className={styles.table}>
        <thead>
          <tr>
            <th>{t('family.invite.token')}</th>
            <th>{t('family.invite.status')}</th>
            <th>{t('family.invite.expires')}</th>
            <th>{t('family.invite.uses')}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {invitations.map(inv => (
            <tr key={inv.id}>
              <td className={styles.truncateCell}>{inv.token}</td>
              <td>
                <Badge variant={inv.active ? 'success' : 'neutral'}>
                  {inv.active ? t('family.invite.active') : t('family.invite.inactive')}
                </Badge>
              </td>
              <td>{inv.expiresAt ? new Date(inv.expiresAt).toLocaleString() : t('family.invite.expires.never')}</td>
              <td>{inv.useCount}{inv.maxUses ? `/${inv.maxUses}` : ''}</td>
              <td>
                <div className={styles.actionRow}>
                  {inv.active && (
                    <Button size="sm" variant={copiedId === inv.id ? 'primary' : 'secondary'} onClick={() => handleCopy(inv)}>
                      {copiedId === inv.id ? t('linkCopied') : t('copyLink')}
                    </Button>
                  )}
                  {inv.active && (
                    <Button size="sm" variant="dangerGhost" onClick={() => handleRevoke(inv.id)}>
                      {t('family.invite.revoke')}
                    </Button>
                  )}
                  {!inv.active && (
                    <Button size="sm" variant="dangerGhost" disabled={deletingId !== null} onClick={() => handleDelete(inv.id)}>
                      {deletingId === inv.id ? t('loading') : t('family.invite.delete')}
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
          {invitations.length === 0 && (
            <tr><td colSpan={5} className={styles.emptyCell}>{t('noData')}</td></tr>
          )}
        </tbody>
      </Table>
      <InviteCreateModal show={showModal} onHide={() => setShowModal(false)} onCreated={handleCreated} />
      {showToast && (
        <div className={styles.toast}>
          <Alert variant="success" dismissible onClose={() => setShowToast(false)}>
            {t('family.invite.created')}
          </Alert>
        </div>
      )}
    </div>
  );
}
